/* eslint-disable react/jsx-filename-extension */
import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useHistory, useLocation } from "react-router-dom";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";
import {
  LuChevronLeft,
  LuChevronRight,
  LuMoon,
  LuPencil,
  LuSun,
} from "react-icons/lu";

import useStoredState from "../../hooks/useStoredState";
import {
  brandIcon as BrandIcon,
  buildMenu,
  findGroupId,
  isGroup,
  readMenuAccess,
} from "./menuItems";

import "./styles/Menu.scss";

const MOBILE_QUERY = "(max-width: 767.98px)";
const THEME_KEY = "@Jurisnet:menuTheme";
const isTheme = (value) => value === "dark" || value === "light";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_QUERY).matches
  );

  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY);
    const onChange = (event) => setIsMobile(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return isMobile;
};

const initialsOf = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return `${first}${last}`.toUpperCase();
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const Aside = ({
  collapsed,
  toggled,
  handleToggleSidebar,
  handleCollapsedChange,
}) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const [theme, setTheme] = useStoredState(THEME_KEY, "dark", isTheme);

  const sections = buildMenu(readMenuAccess(localStorage));
  const activeGroupId = findGroupId(sections, pathname);

  const [openGroups, setOpenGroups] = useState(() =>
    activeGroupId ? [activeGroupId] : []
  );
  const [popupItems, setPopupItems] = useState([]);
  const popup = useRef(null);

  useEffect(() => {
    if (activeGroupId) {
      setOpenGroups((current) =>
        current.includes(activeGroupId) ? current : [...current, activeGroupId]
      );
    }
  }, [activeGroupId]);

  const rail = collapsed && !isMobile;

  let userName = "";
  try {
    userName = JSON.parse(localStorage.getItem("@Jurisnet:user")).name;
  } catch {
    userName = "";
  }

  const closeDrawer = () => {
    if (toggled) handleToggleSidebar(false);
  };

  const toggleGroup = (id) => {
    setOpenGroups((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  };

  const showGroupPopup = (event, group) => {
    setPopupItems([
      { label: group.label, className: "menu-popup-title", disabled: true },
      ...group.items.map((leaf) => ({
        label: leaf.label,
        command: () => history.push(leaf.to),
      })),
    ]);
    popup.current.toggle({
      currentTarget: event.currentTarget,
      target: event.currentTarget,
    });
  };

  const renderLink = (entry) => (
    <NavLink
      key={entry.id}
      to={entry.to}
      exact
      className="menu-item"
      activeClassName="active"
      onClick={closeDrawer}
      data-pr-tooltip={rail ? entry.label : undefined}
      data-pr-position="right"
    >
      <entry.icon className="menu-item-icon" />
      <span className="menu-item-label">{entry.label}</span>
    </NavLink>
  );

  const renderGroup = (group) => {
    const isOpen = openGroups.includes(group.id);
    const isCurrent = group.id === activeGroupId;

    return (
      <div key={group.id} className={`menu-group ${isOpen ? "open" : ""}`}>
        <button
          type="button"
          className={`menu-item ${isCurrent ? "current" : ""}`}
          aria-expanded={rail ? undefined : isOpen}
          data-pr-tooltip={rail ? group.label : undefined}
          data-pr-position="right"
          onClick={(event) =>
            rail ? showGroupPopup(event, group) : toggleGroup(group.id)
          }
        >
          <group.icon className="menu-item-icon" />
          <span className="menu-item-label">{group.label}</span>
          <LuChevronRight className="menu-chevron" />
        </button>
        {isOpen && !rail ? (
          <ul className="menu-sub">
            {group.items.map((leaf) => (
              <li key={leaf.to}>
                <NavLink
                  to={leaf.to}
                  exact
                  className="menu-subitem"
                  activeClassName="active"
                  onClick={closeDrawer}
                >
                  {leaf.label}
                </NavLink>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  };

  return (
    <>
      {toggled ? (
        <div
          className="menu-backdrop"
          onClick={() => handleToggleSidebar(false)}
          role="none"
        />
      ) : null}

      <aside
        className={`app-menu ${rail ? "is-rail" : ""} ${
          toggled ? "is-open" : ""
        }`}
        data-theme={theme}
      >
        <button
          type="button"
          className="menu-edge-toggle"
          onClick={() => handleCollapsedChange(!collapsed)}
          aria-label={collapsed ? "Expandir o menu" : "Recolher o menu"}
          title={collapsed ? "Expandir o menu" : "Recolher o menu"}
        >
          <LuChevronLeft />
        </button>

        <div className="menu-brand">
          <span className="menu-mark">
            <BrandIcon />
          </span>
          <span className="menu-brand-text">JN 2.0</span>
        </div>

        <nav className="menu-nav" aria-label="Menu principal">
          {sections.map((section) => (
            <div key={section.title} className="menu-section">
              <div className="menu-section-title">{section.title}</div>
              {section.entries.map((entry) =>
                isGroup(entry) ? renderGroup(entry) : renderLink(entry)
              )}
            </div>
          ))}
        </nav>

        <div className="menu-footer">
          <Link
            to="/profile"
            className="menu-user"
            onClick={closeDrawer}
            data-pr-tooltip={rail ? "Editar perfil" : undefined}
            data-pr-position="right"
          >
            <span className="menu-avatar">{initialsOf(userName)}</span>
            <span className="menu-user-text">
              <strong>{userName}</strong>
              <small>Editar perfil</small>
            </span>
            <LuPencil className="menu-user-edit" />
          </Link>
          <button
            type="button"
            className="menu-theme"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label={
              theme === "dark" ? "Usar menu claro" : "Usar menu escuro"
            }
            data-pr-tooltip={
              theme === "dark" ? "Usar menu claro" : "Usar menu escuro"
            }
            data-pr-position="right"
          >
            {theme === "dark" ? <LuSun /> : <LuMoon />}
          </button>
        </div>
      </aside>

      <Menu model={popupItems} popup ref={popup} className="menu-popup" />
      {rail ? <Tooltip target=".app-menu [data-pr-tooltip]" /> : null}
    </>
  );
};

export default Aside;
